import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, FileText } from "lucide-react";
import { MaskLines, Reveal } from "../components/motion";
import { StatusBadge } from "../components/StatusBadge";
import { RichText } from "../components/RichText";
import { ContactCTA } from "../components/ContactCTA";
import { mediaUrl, useFetch } from "../lib/api";
import { ease } from "../lib/content";

export const Attachments = ({ items, testPrefix = "attachment" }) => {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return null;
  return (
    <div className="space-y-2">
      {list.map((a, i) => (
        <a
          key={a.url + i}
          href={mediaUrl(a.url)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testPrefix}-${i}`}
          className="group flex items-center justify-between gap-3 border border-teal/15 bg-paper px-4 py-3 text-sm text-teal transition-colors duration-300 hover:border-teal hover:bg-teal hover:text-mist"
        >
          <span className="flex items-center gap-3">
            <FileText className="h-4 w-4 shrink-0" /> {a.name}
          </span>
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
        </a>
      ))}
    </div>
  );
};

export const NotFoundBlock = ({ to, label }) => (
  <main className="flex min-h-[70vh] flex-col items-center justify-center bg-paper px-5 pt-32 text-center" data-testid="not-found">
    <p className="font-display text-6xl text-teal">Not found</p>
    <Link to={to} className="label-caps mt-8 text-teal underline">{label}</Link>
  </main>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const { data: p, loading, error } = useFetch(`/products/${slug}`);
  if (loading) return <main className="min-h-screen bg-teal" />;
  if (error || !p) return <NotFoundBlock to="/products" label="Back to products" />;
  const targets = Array.isArray(p.targets) ? p.targets : [];

  return (
    <main data-testid="product-detail-page">
      <section className="grain relative overflow-hidden bg-teal pb-20 pt-32 text-mist sm:pt-40">
        <div className="relative mx-auto grid max-w-[1440px] gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-7">
            <Link to="/products" data-testid="back-to-products" className="label-caps inline-flex items-center gap-2 !text-[11px] text-mist/80 hover:text-mist">
              <ArrowLeft className="h-4 w-4" /> All products
            </Link>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <StatusBadge status={p.status} testId="product-detail-status" />
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-sage">{p.category}</span>
            </div>
            <MaskLines as="h1" onLoad delay={0.1} lines={[p.name]} testId="product-detail-name" className="mt-6 font-display text-5xl font-normal leading-[1] tracking-[-0.02em] sm:text-7xl" />
            {p.tagline && <p className="mt-4 font-display text-2xl font-light italic text-sage">{p.tagline}</p>}
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-mist/85">{p.summary}</p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <motion.div className="arch aspect-[3/4] overflow-hidden bg-teal-deep" initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.3, ease, delay: 0.2 }}>
              {p.image && <img src={mediaUrl(p.image)} alt={p.name} className="h-full w-full object-cover" />}
            </motion.div>
          </div>
        </div>
      </section>
      <section className="bg-paper py-20 sm:py-28">
        <div className="mx-auto grid max-w-[1440px] gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
          <Reveal className="lg:col-span-7">
            <RichText text={p.description} />
          </Reveal>
          <aside className="space-y-10 lg:col-span-4 lg:col-start-9">
            {targets.length > 0 && (
              <div>
                <p className="label-caps text-moss">Targets & uses</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {targets.map((t) => (
                    <span key={t} className="rounded-full border border-teal/20 px-3 py-1.5 text-sm text-teal">{t}</span>
                  ))}
                </div>
              </div>
            )}
            {(p.attachments || []).length > 0 && (
              <div>
                <p className="label-caps mb-4 text-moss">Documents</p>
                <Attachments items={p.attachments} testPrefix="product-attachment" />
              </div>
            )}
            <Link
              to={`/contact?subject=Products&about=${encodeURIComponent(p.name)}`}
              data-testid="product-enquire-btn"
              className="group label-caps flex items-center justify-between rounded-full bg-teal px-7 py-4 !text-[11px] text-mist transition-colors duration-300 hover:bg-teal-ink"
            >
              Enquire about this product <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
          </aside>
        </div>
      </section>
      <ContactCTA />
    </main>
  );
}
